/*
 * OrbitTemplate — Stage 2 outcome `template_direct`.
 *
 * Triggered after a user picks the Template card in Stage 1 and Stage 2
 * confirms bypass eligible (clarity ≥ 0.75). A pre-drafted PRD is seeded
 * from the closest template + the user's L1 dossier. They review +
 * tweak before firing the validx project.
 *
 * Flow:
 *   1. Fetch engagement via /api/intake/engagement/:eid
 *   2. Render the seeded PRD outline + L1 context
 *   3. CTA "Accept & build" → POST /api/agents/projects with the
 *      engagement_id + tier → validx creates the project → user lands
 *      on /agents/:projectId.
 *
 * Cost shape: burns 1 agent_request. The quota was already verified at
 * /stage1/pick (Phase 2 guard); this surface assumes headroom is there.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  FileText, ChevronLeft, ChevronRight, Loader2, CheckCircle2, Hammer,
} from 'lucide-react';


const API_BASE = process.env.REACT_APP_API_URL ?? '';


export default function OrbitTemplate() {
  const navigate = useNavigate();
  const { engagementId } = useParams<{ engagementId: string }>();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [building, setBuilding] = useState(false);
  const [buildErr, setBuildErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetch = async () => {
      if (!engagementId) return;
      try {
        const res = await axios.get(`${API_BASE}/api/intake/engagement/${engagementId}`);
        if (!cancelled) setData(res.data);
      } catch (err: any) {
        if (!cancelled) {
          const detail = err?.response?.data?.detail;
          setError(typeof detail === 'string' ? detail : 'Could not load engagement.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetch();
    return () => { cancelled = true; };
  }, [engagementId]);

  const handleAcceptAndBuild = async () => {
    if (!data || building) return;
    setBuilding(true);
    setBuildErr(null);
    try {
      const summary = data.l1_summary || {};
      const title = (summary.company_name
        ? `${summary.company_name} — `
        : '') + (data.stage1_solution?.name || 'Template build');
      const description = summary.problem_summary
        || summary.acceptance_criteria
        || 'Custom build seeded from a marketplace template.';
      const res = await axios.post(`${API_BASE}/api/agents/projects`, {
        title,
        description,
        type: 'FEATURE',
        priority: 'NORMAL',
        orbitEngagementId: data.engagement_id,
      });
      // Workspace lives on validx now — land on the agents list (its rows
      // open the validx builder via the HMAC bridge) instead of the retired
      // in-app /agents/[id] viewer.
      navigate('/agents');
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setBuildErr(typeof detail === 'string' ? detail : 'Could not start the build. Try again.');
      setBuilding(false);
    }
  };

  return (
    <div style={shellStyles.page}>
      <div style={shellStyles.card}>
        <button
          type="button"
          onClick={() => navigate('/my-business#routing')}
          style={shellStyles.backLink}
        >
          <ChevronLeft size={14} strokeWidth={2} />
          Back to routing
        </button>

        <div style={{ ...shellStyles.iconCircle, background: 'rgba(255,234,0,0.18)', color: '#7a6500' }}>
          <FileText size={26} strokeWidth={2} />
        </div>

        <div style={shellStyles.eyebrow}>Stage 2 · Template direct</div>

        {loading ? (
          <p style={shellStyles.bodyText}>
            <Loader2 size={14} className="animate-spin" style={{ marginRight: 6 }} />
            Reading your engagement…
          </p>
        ) : error ? (
          <>
            <h1 style={shellStyles.title}>Couldn't load this engagement.</h1>
            <p style={shellStyles.bodyText}>{error}</p>
          </>
        ) : data ? (
          <>
            <h1 style={shellStyles.title}>
              Your build, <em style={{ fontFamily: "var(--font-serif, 'IBM Plex Serif', serif)" }}>started</em>.
            </h1>

            <p style={shellStyles.bodyText}>
              We seeded a PRD from the closest template plus what you've told us.
              Accept it to spin up your build, or edit the brief on the next screen.
            </p>

            {/* L1 summary recap */}
            <div style={shellStyles.summaryBlock}>
              <div style={shellStyles.summaryEyebrow}>What we have on file</div>
              <ul style={shellStyles.summaryList}>
                {data.l1_summary?.company_name && (
                  <li><strong>Company.</strong> {data.l1_summary.company_name}</li>
                )}
                {data.l1_summary?.industry && (
                  <li><strong>Industry.</strong> {data.l1_summary.industry}</li>
                )}
                {data.l1_summary?.problem_summary && (
                  <li><strong>Problem.</strong> {data.l1_summary.problem_summary}</li>
                )}
                {data.l1_summary?.acceptance_criteria && (
                  <li><strong>Done looks like.</strong> {data.l1_summary.acceptance_criteria}</li>
                )}
                {data.stage1_solution?.name && (
                  <li>
                    <strong>Template.</strong> {data.stage1_solution.name}
                    {data.stage1_solution.tagline ? ` — ${data.stage1_solution.tagline}` : ''}
                  </li>
                )}
              </ul>
            </div>

            <p style={shellStyles.note}>
              <CheckCircle2 size={12} style={{ marginRight: 6, verticalAlign: '-1px' }} strokeWidth={2.5} />
              Burns 1 agent build from your {data.tier_at_decision || 'free'} plan. Phases visible
              in the workspace; Phase 1 ships first.
            </p>

            {buildErr && (
              <p style={shellStyles.errorText}>{buildErr}</p>
            )}

            <div style={shellStyles.actions}>
              <button
                type="button"
                onClick={handleAcceptAndBuild}
                disabled={building}
                style={shellStyles.primaryCta}
              >
                {building
                  ? <Loader2 size={14} className="animate-spin" />
                  : <Hammer size={14} strokeWidth={2.25} />}
                <span>{building ? 'Spinning up your build…' : 'Accept & build'}</span>
                <ChevronRight size={14} strokeWidth={2.5} />
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}


const shellStyles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f8f9fb',
    padding: 24,
    fontFamily: "var(--font-sans, 'IBM Plex Sans', system-ui, sans-serif)",
  },
  card: {
    maxWidth: 600,
    width: '100%',
    background: '#fff',
    border: '1px solid #e2e5eb',
    borderRadius: 16,
    padding: '40px 40px 32px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
  },
  backLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    background: 'transparent',
    border: 0,
    color: '#545454',
    fontSize: 12,
    cursor: 'pointer',
    padding: 0,
    marginBottom: 20,
  },
  iconCircle: {
    width: 56,
    height: 56,
    borderRadius: 14,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 18,
  },
  eyebrow: {
    fontFamily: "var(--font-sans, 'IBM Plex Sans', system-ui, sans-serif)",
    fontSize: 10.5,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: '#7a6500',
    lineHeight: 1.3,
    marginBottom: 10,
  },
  title: {
    fontSize: 26,
    fontWeight: 700,
    letterSpacing: '-0.015em',
    color: '#000',
    margin: '0 0 12px',
    lineHeight: 1.25,
  },
  bodyText: {
    fontSize: 14.5,
    color: '#545454',
    lineHeight: 1.55,
    margin: '0 0 20px',
  },
  summaryBlock: {
    background: '#eef3ff',
    border: '1px solid rgba(24,25,29,0.12)',
    borderRadius: 12,
    padding: '14px 16px',
    marginBottom: 20,
  },
  summaryEyebrow: {
    fontFamily: "var(--font-sans, 'IBM Plex Sans', system-ui, sans-serif)",
    fontSize: 10,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: '#18191d',
    marginBottom: 8,
  },
  summaryList: {
    margin: 0,
    paddingLeft: 18,
    color: '#000',
    fontSize: 13.5,
    lineHeight: 1.6,
  },
  note: {
    fontSize: 12,
    color: '#545454',
    background: '#f7f9fc',
    border: '1px solid #e2e5eb',
    borderRadius: 8,
    padding: '10px 12px',
    marginBottom: 20,
    lineHeight: 1.55,
  },
  errorText: {
    fontSize: 12,
    color: '#fd2751',
    margin: '0 0 12px',
  },
  actions: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
  },
  primaryCta: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '11px 18px',
    background: '#18191d',
    color: '#fff',
    border: 0,
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
};
